import React, { useCallback, useEffect, useRef, useState } from 'react';
import core, { App } from '@gin-betting/core';
import ResizeObserver from 'resize-observer-polyfill';

import { MIN_WIDTH_FOR_DOUBLE_MODE } from '../../constants';
import { ViewerMode } from '../../types';
import { getCurrentBetradarSize } from '../../utils';
import useBetradar from './useBetradar';

const {
    utils: { isInnerPage },
} = core;

interface Input {
    isFullscreenActive: boolean;
    isSmall: boolean;
    betradarId: string | null;
    viewerMode: ViewerMode;
}

interface Output {
    styleProps: {} | { width: number | string; height: number | string };
    initRef: (e: HTMLElement | null) => void;
    loadScriptStatus: App.FetchStatus;
}

const useMatchTracker = ({
    isFullscreenActive,
    isSmall,
    betradarId,
    viewerMode,
}: Input): Output => {
    const widthObserver = useRef<typeof ResizeObserver.prototype>();
    const observedNode = useRef<Element>();
    const [clientSize, setClientSize] = useState<[number, number]>([0, 0]);

    const [clientWidth, clientHeight] = clientSize;

    const { loadScriptStatus, pathname } = useBetradar({
        betradarId,
        viewerMode,
        isContainerCompact: !!(
            observedNode.current &&
            observedNode.current.clientWidth < MIN_WIDTH_FOR_DOUBLE_MODE
        ),
        isSmall,
    });

    const isListPage = !isInnerPage(pathname);

    const handleResize = (entries: Array<{ target: Element | undefined }>) => {
        const entry = entries[0]?.target;

        if (!entry) return;

        setClientSize([entry.clientWidth, entry.clientHeight]);
    };

    useEffect(() => {
        if (!widthObserver.current) return;

        handleResize([{ target: observedNode.current }]);
    }, [isSmall, pathname]);

    const initObserver = useCallback(() => {
        widthObserver.current = new ResizeObserver(handleResize);

        if (observedNode.current) {
            widthObserver.current.observe(observedNode.current);
        }
    }, []);

    const destroyObserver = useCallback(() => {
        if (widthObserver.current) {
            widthObserver.current.unobserve(observedNode.current);
            observedNode.current = undefined;
        }
    }, []);

    const initRef = useCallback(
        node => {
            if (!node && widthObserver.current) {
                destroyObserver();
            }

            observedNode.current = node;

            initObserver();
        },
        [initObserver, destroyObserver]
    );

    const styleProps: React.CSSProperties = {};

    const [width, height] =
        clientWidth !== 0 && clientHeight !== 0
            ? getCurrentBetradarSize({
                  clientWidth,
                  clientHeight,
                  isSmall,
                  isFullscreenActive,
                  isListPage,
              })
            : [0, 0];

    if (width) {
        styleProps.width = width;
        styleProps.height = height;
    }

    return {
        styleProps,
        initRef,
        loadScriptStatus,
    };
};

export default useMatchTracker;
